/** LOTE 4 (founder 14-oct-2026) — crear/editar/borrar bloqueos y descansos, de un día o recurrentes,
 *  con «Solo este día» / «Toda la serie» y lista de citas en conflicto (nunca se cancelan). */
import { useEffect, useMemo, useState } from 'react';
import { Repeat, Trash2, Ban, Coffee, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TimeSelect } from '@/components/Profile/TimeSelect';
import { bookingApi } from '@/lib/bookingApi';
import { tr, getCurrentLang } from '@/contexts/LanguageContext';
import { formatTime12 } from '@/utils/bookingFormat';

const DAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const DAYS_EN = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const ScopeChooser = ({ title, onPick, onCancel, busy }) => (
  <div className="rounded-xl border border-border bg-secondary/40 p-3 space-y-2" data-testid="block-scope-chooser">
    <p className="text-sm font-semibold text-foreground">{title}</p>
    <div className="grid grid-cols-2 gap-2">
      <Button variant="outline" disabled={busy} onClick={() => onPick('day')} className="rounded-full h-9 text-xs" data-testid="block-scope-day">{tr("Solo este día")}</Button>
      <Button disabled={busy} onClick={() => onPick('series')} className="rounded-full h-9 text-xs" data-testid="block-scope-series">{tr("Toda la serie")}</Button>
    </div>
    <button type="button" onClick={onCancel} className="text-xs text-muted-foreground underline" data-testid="block-scope-cancel">{tr("Atrás")}</button>
  </div>
);

export const ConflictList = ({ conflicts, onContinue, onBack, busy }) => (
  <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="block-conflicts">
    <p className="text-sm font-semibold text-foreground inline-flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-400" />{tr("{0} cita(s) en ese tramo", conflicts.length)}</p>
    <p className="text-xs text-muted-foreground">{tr("Las citas se mantienen tal cual: no se cancelan. Puedes continuar o cambiar el bloqueo.")}</p>
    <ul className="max-h-40 overflow-y-auto space-y-1" data-testid="block-conflicts-list">
      {conflicts.map((c) => (
        <li key={`${c.booking_id}-${c.date}`} className="text-xs text-foreground flex justify-between gap-2" data-testid={`block-conflict-${c.booking_id}`}>
          <span className="truncate">{c.date} · {formatTime12(c.start_time)}–{formatTime12(c.end_time)}</span>
          <span className="truncate text-muted-foreground">{c.client_name || tr("Cliente")}{c.service_name ? ` · ${c.service_name}` : ''}</span>
        </li>
      ))}
    </ul>
    <div className="grid grid-cols-2 gap-2 pt-1">
      <Button variant="outline" disabled={busy} onClick={onBack} className="rounded-full h-9 text-xs" data-testid="block-conflicts-back">{tr("Cambiar el bloqueo")}</Button>
      <Button disabled={busy} onClick={onContinue} className="rounded-full h-9 text-xs" data-testid="block-conflicts-continue">{tr("Continuar")}</Button>
    </div>
  </div>
);

/**
 * props: open, onOpenChange, date (YYYY-MM-DD), barberId (barbería con agenda_access), onSaved,
 * initial: {start,end} para crear · block: {id,start,end,name,kind,recurring} para editar.
 */
export const BlockEditorSheet = ({ open, onOpenChange, date, barberId = null, initial = null, block = null, onSaved }) => {
  const editing = Boolean(block?.id);
  const [name, setName] = useState('');
  const [start, setStart] = useState('13:00');
  const [end, setEnd] = useState('14:00');
  const [repeat, setRepeat] = useState(false);
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4]);
  const [until, setUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [pendingScope, setPendingScope] = useState(null); // 'save' | 'delete'
  const [scope, setScope] = useState(null);

  useEffect(() => {
    if (!open) return;
    setConflicts(null); setPendingScope(null); setScope(null);
    setName(block?.name || '');
    setStart(block?.start || initial?.start || '13:00');
    setEnd(block?.end || initial?.end || '14:00');
    const rep = block?.repeat;
    setRepeat(Boolean(rep) || block?.kind === 'break');
    setWeekdays(rep?.weekdays?.length ? rep.weekdays : (block?.kind === 'break' && date ? [(new Date(`${date}T12:00:00`).getDay() + 6) % 7] : [0, 1, 2, 3, 4]));
    setUntil(rep?.until || '');
  }, [open, block, initial, date]);

  const dayLabels = getCurrentLang() === 'en' ? DAYS_EN : DAYS;
  const valid = useMemo(() => start < end && (!repeat || weekdays.length > 0) && (!until || until >= date), [start, end, repeat, weekdays, until, date]);
  const isSeries = editing && (block?.recurring || block?.kind === 'break');

  const close = () => onOpenChange(false);

  const doSave = async (chosenScope, confirm = false) => {
    setBusy(true);
    try {
      let res;
      if (!editing) {
        res = await bookingApi.createBlock({
          barber_id: barberId || undefined, date, start, end, name: name || null,
          repeat: repeat ? { weekdays, until: until || null } : null, confirm,
        });
        toast.success(repeat ? tr("Bloqueo recurrente creado") : tr("Horario bloqueado — ya no acepta reservas"));
      } else {
        res = await bookingApi.editBlock(block.id, {
          barber_id: barberId || undefined, scope: chosenScope || 'series', date, start, end, name: name || null,
          ...(chosenScope === 'series' && block.kind !== 'break' ? (repeat ? { repeat: { weekdays, until: until || null } } : { clear_repeat: true }) : {}),
          confirm,
        });
        toast.success(chosenScope === 'day' ? tr("Cambiado solo este día") : tr("Bloqueo actualizado"));
      }
      onSaved?.(res);
      close();
    } catch (err) {
      if (err?.status === 409 && err?.payload?.detail?.code === 'block_conflicts') {
        setConflicts({ list: err.payload.detail.conflicts, action: 'save', scope: chosenScope });
      } else {
        toast.error(err?.message || tr("No pudimos guardar el bloqueo"));
      }
    } finally { setBusy(false); }
  };

  const doDelete = async (chosenScope) => {
    setBusy(true);
    try {
      await bookingApi.deleteBlock(block.id, { barber_id: barberId || undefined, scope: chosenScope || 'series', date });
      toast.success(chosenScope === 'day' ? tr("Quitado solo este día") : tr("Bloqueo eliminado"));
      onSaved?.();
      close();
    } catch (err) { toast.error(err?.message || tr("No pudimos borrar el bloqueo")); } finally { setBusy(false); }
  };

  const onSaveClick = () => { if (isSeries) setPendingScope('save'); else doSave('series'); };
  const onDeleteClick = () => { if (isSeries) setPendingScope('delete'); else doDelete('series'); };

  const Icon = block?.kind === 'break' ? Coffee : Ban;
  const title = editing ? (block?.kind === 'break' ? tr("Descanso") : tr("Bloqueo")) : tr("Bloquear horario");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[92vh] overflow-y-auto" data-testid="block-editor-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display inline-flex items-center gap-2"><Icon className="w-5 h-5 text-primary" />{title}
            {isSeries && <span className="inline-flex items-center gap-1 text-[10px] font-normal text-muted-foreground border border-border rounded-full px-2 py-0.5"><Repeat className="w-3 h-3" />{tr("Se repite")}</span>}
          </SheetTitle>
        </SheetHeader>

        {conflicts ? (
          <div className="mt-4">
            <ConflictList conflicts={conflicts.list} busy={busy} onBack={() => setConflicts(null)} onContinue={() => doSave(conflicts.scope, true)} />
          </div>
        ) : pendingScope ? (
          <div className="mt-4">
            <ScopeChooser
              title={pendingScope === 'delete' ? tr("¿Qué quieres borrar?") : tr("¿Qué quieres cambiar?")}
              busy={busy}
              onCancel={() => setPendingScope(null)}
              onPick={(sc) => { setScope(sc); setPendingScope(null); if (pendingScope === 'delete') doDelete(sc); else doSave(sc); }}
            />
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <p className="text-xs text-muted-foreground" data-testid="block-editor-date">{date}</p>
            <div className="flex items-center gap-2">
              <TimeSelect value={start} onChange={setStart} testId="block-start" />
              <span className="text-muted-foreground text-xs">{tr("a")}</span>
              <TimeSelect value={end} onChange={setEnd} testId="block-end" />
            </div>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={tr("Nombre (opcional), ej. Almuerzo")} className="bg-background h-10" data-testid="block-name-input" />

            {block?.kind !== 'break' && (
              <div className="rounded-xl border border-border p-3 space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm font-semibold text-foreground inline-flex items-center gap-1.5"><Repeat className="w-4 h-4 text-primary" />{tr("Repetir")}</span>
                  <button type="button" role="switch" aria-checked={repeat} onClick={() => setRepeat((v) => !v)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors ${repeat ? 'bg-primary border-primary' : 'bg-secondary border-border'}`}
                    data-testid="block-repeat-switch">
                    <span className={`inline-block h-5 w-5 rounded-full bg-background shadow transition-transform ${repeat ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </label>
                {repeat && (
                  <>
                    <div className="flex items-center gap-1.5" data-testid="block-weekdays">
                      {dayLabels.map((l, i) => (
                        <button key={i} type="button" onClick={() => setWeekdays((w) => (w.includes(i) ? w.filter((x) => x !== i) : [...w, i].sort()))}
                          className={`w-8 h-8 rounded-full text-xs font-bold border ${weekdays.includes(i) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}
                          data-testid={`block-weekday-${i}`} aria-pressed={weekdays.includes(i)}>{l}</button>
                      ))}
                      <button type="button" onClick={() => setWeekdays([0, 1, 2, 3, 4, 5, 6])} className="ml-1 text-[11px] text-primary underline" data-testid="block-weekdays-all">{tr("Todos")}</button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{tr("Hasta")}</span>
                      <Input type="date" value={until} min={date} onChange={(e) => setUntil(e.target.value)} className="bg-background h-9 text-sm flex-1" data-testid="block-until-input" />
                      {until ? <button type="button" onClick={() => setUntil('')} className="text-[11px] text-muted-foreground underline" data-testid="block-until-clear">{tr("Sin fecha de fin")}</button>
                        : <span className="text-[11px] text-muted-foreground">{tr("Sin fecha de fin")}</span>}
                    </div>
                  </>
                )}
              </div>
            )}
            {block?.kind === 'break' && <p className="text-[11px] text-muted-foreground">{tr("Descanso de tu horario semanal. «Toda la serie» lo cambia en todos los días que lo comparten; «Solo este día» aplica únicamente al {0}.", date)}</p>}

            <div className="flex items-center gap-2 pt-1">
              {editing && (
                <Button variant="outline" onClick={onDeleteClick} disabled={busy} className="rounded-full h-10 px-3 text-destructive border-destructive/40" data-testid="block-delete-btn">
                  <Trash2 className="w-4 h-4" /></Button>
              )}
              <Button onClick={onSaveClick} disabled={busy || !valid} className="rounded-full h-10 flex-1 font-bold" data-testid="block-save-btn">
                {editing ? tr("Guardar") : (repeat ? tr("Crear bloqueo recurrente") : tr("Bloquear"))}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
